import { z } from 'zod';
import { idSchema } from './common.ts';

// Smart slide templates (docs/09 §B1). Templates are first-party, bundled in the worker;
// the CMS codes against these response shapes. A template is a self-contained HTML doc
// with {{key}} placeholders and a declared list of editable fields. Field VALUES live on
// media.fields_json and are mutable without regeneration (a field edit re-renders the R2
// HTML in place). These schemas are the fixed contract the CMS agent builds its editor on.

// The editable field kinds a template exposes.
export const templateFieldTypeSchema = z.enum(['text', 'multiline', 'color', 'image_media_id']);
export type TemplateFieldType = z.infer<typeof templateFieldTypeSchema>;

// One editable field in a template's spec (GET /v1/templates → items[].fields[]).
export const templateFieldSchema = z.object({
  key: z.string(), // the {{key}} placeholder / fields_json property
  label: z.string(), // human label for the CMS form
  type: templateFieldTypeSchema,
  max_len: z.number().int().positive().optional(), // for text/multiline
  default: z.string().optional(), // default value (image_media_id has none)
});
export type TemplateField = z.infer<typeof templateFieldSchema>;

// A template as returned by GET /v1/templates.
export const templateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  fields: z.array(templateFieldSchema),
  aspect: z.literal('16:9'),
});
export type Template = z.infer<typeof templateSchema>;

export const templateListSchema = z.object({ items: z.array(templateSchema) });
export type TemplateList = z.infer<typeof templateListSchema>;

// Field values map: { [key]: string }. Values are validated against the template spec in
// the service (type, max_len, image ownership) and HTML-escaped at render time.
export const templateFieldsSchema = z.record(z.string(), z.string());
export type TemplateFields = z.infer<typeof templateFieldsSchema>;

// POST /v1/templates/:id/preview → render (not persisted) for a live iframe.
export const templatePreviewSchema = z.object({ fields: templateFieldsSchema.default({}) });
export type TemplatePreview = z.infer<typeof templatePreviewSchema>;

// POST /v1/media/from-template → render → R2 → media (kind=html_template, status=ready).
export const mediaFromTemplateSchema = z.object({
  template_id: z.string().min(1),
  name: z.string().min(1),
  fields: templateFieldsSchema.default({}),
});
export type MediaFromTemplate = z.infer<typeof mediaFromTemplateSchema>;

// PATCH /v1/media/:id/fields → validate + re-render the html_template media in place.
export const updateMediaFieldsSchema = z.object({ fields: templateFieldsSchema });
export type UpdateMediaFields = z.infer<typeof updateMediaFieldsSchema>;

// Aspect ratios an image can be generated at (docs/09 §B4). '16:9' matches the slide canvas.
export const genAspectSchema = z.enum(['16:9', '9:16', '1:1']);
export type GenAspect = z.infer<typeof genAspectSchema>;

// POST /v1/media/from-snippet-esque path is not a public REST contract; snippet creation
// happens via the assistant tool. Kept here so the CMS can render a saved snippet's meta.
export const snippetMetaSchema = z.object({
  media_id: idSchema,
  size_bytes: z.number().int().nonnegative(),
});
export type SnippetMeta = z.infer<typeof snippetMetaSchema>;
